import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Spinner } from 'elmo-elements';
import { BreatheLaunchParamType } from './routes';
import {
  getPublishedEntityLocation,
  getCurrentFrame,
  hasVocamData,
  processQuestion,
  replaceContentAssetLink,
  useIsMobileScreenSize,
} from '@/lib/util';
import { appData } from '@/lib/data';
import { signResourceUrls } from '@/lib/util/signResourceUrls';
import PlayerUi from '@/components/Player';
import { AnswerDataType } from '@/modules/types/question';
import { useThemeStyle } from '@/modules/hooks/useThemeStyle';
import { useModuleStyle } from '@/modules/hooks/useModuleStyle';
import {
  useGetBreatheLaunchQuery,
  useGetBreatheVocamVideoLinkQuery,
  useGetElearningJsonQuery,
  useGetQuizJsonQuery,
  useSubmitBreatheCompletionMutation,
} from '@/modules/services';
import {
  DEFAULT_ELEARNING_SETTINGS,
  DEFAULT_QUESTION_COUNT,
  ERROR_MESSAGES,
  QUIZ_START_DEBOUNCE_MS,
  SCORE_PERCENTAGE_BASE,
} from './constants';

const BreatheLaunch = (): ReactElement => {
  const { tenantId, elearningId } = useParams<BreatheLaunchParamType>();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const breatheToken = query.get('bt') ?? '';
  const { isMobile } = useIsMobileScreenSize();

  const [currentFrameId, setCurrentFrameId] = useState('');
  const [isQuizReady, setIsQuizReady] = useState(false);

  const {
    data: launchData,
    error: launchError,
    isLoading: launchLoading,
  } = useGetBreatheLaunchQuery(
    { tenantId, elearningId, breatheToken },
    { skip: !breatheToken },
  );

  const identifier = launchData?.identifier;

  const { data: elearningData, isLoading: elearningLoading } =
    useGetElearningJsonQuery(
      {
        tenantId: launchData?.tenantId || '',
        type: 'live',
        entityId: launchData?.elearningId || '',
        identifier: identifier ? Number(identifier) : undefined,
        entity: 'elearnings',
        contentType: 'published-content',
      },
      { skip: !launchData },
    );

  const { data: quizData, isLoading: quizLoading } = useGetQuizJsonQuery(
    {
      tenantId: launchData?.tenantId || '',
      type: 'live',
      entityId: launchData?.elearningId || '',
      identifier: identifier ? Number(identifier) : undefined,
      entity: 'quizzes',
      contentType: 'published-content',
    },
    { skip: !launchData },
  );

  const [submitCompletion, { isError: completionError }] =
    useSubmitBreatheCompletionMutation();

  const currentFrame = useMemo(
    () =>
      getCurrentFrame(
        elearningData?.frames ?? [],
        currentFrameId || elearningData?.frames?.[0]?.id || '',
      ),
    [elearningData?.frames, currentFrameId],
  );

  const { data: vocamVideoLink, isLoading: vocamLoading } =
    useGetBreatheVocamVideoLinkQuery(
      {
        breatheToken,
        tenantId: launchData?.tenantId ?? '',
        moduleId: currentFrame?.vocamMetadata?.moduleId as number,
        topicId: currentFrame?.vocamMetadata?.topicId as number,
      },
      {
        skip:
          !breatheToken ||
          !launchData?.tenantId ||
          !currentFrame?.vocamMetadata?.moduleId ||
          !currentFrame?.vocamMetadata?.topicId ||
          !hasVocamData(currentFrame),
      },
    );

  const isLoading =
    launchLoading || elearningLoading || quizLoading || vocamLoading;

  const dataError = useMemo(() => {
    if (!breatheToken) return ERROR_MESSAGES.MISSING_TOKEN;
    if (launchError) return ERROR_MESSAGES.FAILED_TO_LAUNCH;
    return null;
  }, [breatheToken, launchError]);

  useThemeStyle(elearningData?.theme);
  useModuleStyle(elearningData);

  const elearningBehaviourSettings = useMemo(
    () => ({
      tenantId: launchData?.tenantId || '',
      ...DEFAULT_ELEARNING_SETTINGS,
    }),
    [launchData?.tenantId],
  );

  const submitBreatheCompletion = useCallback(
    async (score: number, responseDetails: Record<string, unknown>) => {
      if (!launchData) return;

      await submitCompletion({
        tenantId,
        elearningId,
        breatheToken,
        uid: launchData.uid,
        mid: launchData.mid,
        cid: launchData.cid,
        score,
        responseDetails,
      });
    },
    [launchData, breatheToken, tenantId, elearningId, submitCompletion],
  );

  const playerHandlers = useMemo(() => {
    if (!quizData) return undefined;

    const playerHandlerState = {
      questionsToAsk: quizData.quizMetadata?.questionNo ?? 0,
      questionsAsked: 0,
      correctAnswers: 0,
      modifiedQuestions: quizData.questions ?? [],
      questionsCompleted: [] as any[],
      hasStarted: false,
    };

    return {
      submitResult: (result: any, callback: () => void) => {
        const score = Math.round(
          (playerHandlerState.correctAnswers /
            (quizData.quizMetadata?.questionNo || DEFAULT_QUESTION_COUNT)) *
            SCORE_PERCENTAGE_BASE,
        );

        const passThreshold = quizData.quizMetadata?.pass;

        if (passThreshold != null && score >= passThreshold) {
          submitBreatheCompletion(score, {
            questionCompleted: playerHandlerState.questionsCompleted,
            quizId: quizData.id,
            pass: passThreshold,
            score,
          });
        }

        playerHandlerState.hasStarted = false;
        if (callback) callback();
      },
      submitQuestion: (
        answer: AnswerDataType,
        callback: (payload: any) => void,
      ) => {
        const nextPayload = processQuestion(answer, playerHandlerState);
        callback(nextPayload);
      },
      getQuestions: () => {
        playerHandlerState.questionsAsked = 0;
        playerHandlerState.correctAnswers = 0;
        playerHandlerState.modifiedQuestions = quizData.questions ?? [];
        playerHandlerState.questionsCompleted = [];
      },
      submitAssessment: (
        assessmentData: any,
        callback: (payload: { score: number }) => void,
      ) => {
        const score = Math.round(
          (playerHandlerState.correctAnswers /
            (quizData.quizMetadata?.questionNo || DEFAULT_QUESTION_COUNT)) *
            SCORE_PERCENTAGE_BASE,
        );
        callback({ score });
      },
      startAssessment: () => {
        if (!playerHandlerState.hasStarted) {
          playerHandlerState.hasStarted = true;
          setIsQuizReady(true);
          setTimeout(() => {
            playerHandlerState.hasStarted = false;
          }, QUIZ_START_DEBOUNCE_MS);
        }
      },
      moveToFrame: (nextFrameId: string) => {
        setCurrentFrameId(nextFrameId);
      },
    };
  }, [quizData, submitBreatheCompletion]);

  if (!breatheToken) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Unable to load course</h2>
        <p>{ERROR_MESSAGES.MISSING_TOKEN}</p>
      </div>
    );
  }

  if (dataError) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Unable to load course</h2>
        <p>{dataError}</p>
      </div>
    );
  }

  if (isLoading || !launchData || !elearningData?.frames) {
    return <Spinner />;
  }

  const publishedAssetLink = getPublishedEntityLocation({
    contentType: 'published-content-assets',
    tenantId: launchData.tenantId,
    type: 'live',
    entity: 'elearnings',
    entityId: launchData.elearningId,
    identifier: Number(launchData.identifier),
  });

  const frames = elearningData.frames.map((frame: any) => {
    const content = replaceContentAssetLink({
      content: frame.content,
      newLink: publishedAssetLink,
      tenantId: launchData.tenantId,
      elearningId: launchData.elearningId,
    });
    return { ...frame, content };
  });

  const elearning = { ...elearningData };
  if (!elearning.theme) {
    elearning.theme = appData.playerTheme.default;
  }

  return (
    <>
      {completionError && (
        <div
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#fff3cd',
            color: '#856404',
            textAlign: 'center',
          }}
        >
          {ERROR_MESSAGES.COMPLETION_FAILED}
        </div>
      )}
      <PlayerUi
        isPlayable={true}
        isLive={true}
        currentFrameId={currentFrameId || frames[0]?.id || ''}
        isQuizEnabled={isQuizReady}
        elearningBehaviourSettings={elearningBehaviourSettings}
        elearning={elearning}
        frames={frames}
        quiz={quizData}
        resources={signResourceUrls(
          elearning.resources,
          launchData.resourceSignature,
        )}
        helperContent={appData.helpJson.default.content as any[]}
        helperFaq={appData.helpJson.default.faqs as any[]}
        handlers={playerHandlers}
        isMobileView={isMobile}
        vocamVideoLink={vocamVideoLink}
      />
    </>
  );
};

export default BreatheLaunch;
